import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { FastMonetisationDiscoveryBanner } from "../components/FastMonetisationDiscoveryBanner";
import { FounderBlock } from "../components/FounderBlock";
import { WhatCouldYouBuild } from "../components/WhatCouldYouBuild";
import { ChatGPTOutputShowcase } from "../components/ChatGPTOutputShowcase";
import { SiteFooter } from "../components/SiteFooter";
import { SocialMeta } from "../components/SocialMeta";
import { BirthdayBanner, BirthdayPromoSection } from "../components/BirthdayBanner";
import { LandingFaq } from "../components/landing/LandingFaq";
import { LandingChoice } from "../components/landing/LandingChoice";
import { NewInVaultAnnouncement } from "../components/NewInVaultAnnouncement";
import { LandingHeader } from "../components/landing/LandingHeader";
import { LandingHero } from "../components/landing/LandingHero";
import { LandingProductPreview } from "../components/landing/LandingProductPreview";
import { LandingStickyChoiceBar } from "../components/landing/LandingStickyChoiceBar";
import { LandingLifetimeOffer } from "../components/landing/LandingLifetimeOffer";
import { LandingNarrative } from "../components/landing/LandingNarrative";
import { LandingProofBand } from "../components/landing/LandingProofBand";
import { LandingTrackPreview } from "../components/landing/LandingTrackPreview";
import { useAuth } from "../contexts/AuthContext";
import { useUtmParams } from "../hooks/useUtmParams";
import { api } from "../lib/api";
import { buildLifetimeSignupHref } from "../lib/landingLinks";
import { VAULT } from "../data/vaultStats";
import { trackEvent } from "../lib/analytics";

export default function PublicLandingPage() {
  const { user } = useAuth();
  const utm = useUtmParams();
  const [loaded, setLoaded] = useState(false);
  const [stats, setStats] = useState(null);
  const [heroVariant, setHeroVariant] = useState("v3");

  useEffect(() => { setLoaded(true); trackEvent("LandingPageView", { page: "home" }); }, []);
  useEffect(() => {
    let active = true;
    Promise.allSettled([api.get("/billing/lifetime-stats"), api.get("/public/site-settings")]).then(([statsResult, settingsResult]) => {
      if (!active) return;
      if (statsResult.status === "fulfilled") setStats(statsResult.value.data);
      const hv = settingsResult.status === "fulfilled" ? settingsResult.value.data?.hero_variant : null;
      const variant = ["v1", "v2", "v3"].includes(hv) ? hv : "v3";
      setHeroVariant(variant);
      try { localStorage.setItem("sv_hero_variant", variant); } catch { /* private mode */ }
    });
    return () => { active = false; };
  }, []);

  const lifetimeHref = useMemo(() => buildLifetimeSignupHref(utm), [utm]);
  const homeJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Stag Vault Complete",
    description: `Lifetime access to ${VAULT.promptsFmt} AI prompts organised into ${VAULT.tracks} sequenced income playbooks.`,
    brand: { "@type": "Brand", name: "Stag Vault" },
    offers: {
      "@type": "Offer",
      price: String(stats?.price || 25),
      priceCurrency: "GBP",
      availability: stats?.sold_out ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      url: "https://stagvault.co.uk/pricing",
    },
  }), [stats?.price, stats?.sold_out]);
  if (user && typeof user === "object") return <Navigate to="/vault" replace />;

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#030206] text-white" data-testid="public-landing-page">
      <SocialMeta title={`Stag Vault — ${VAULT.tracks} AI income playbooks · £25 lifetime`} description={`Build something sellable with AI. ${VAULT.promptsFmt} quality-reviewed prompts across ${VAULT.tracks} sequenced income playbooks, from idea to shipped product or service. Stag Vault Complete (Lifetime Access) is £25.`} canonical="https://stagvault.co.uk/" url="https://stagvault.co.uk/" image="https://stagvault.co.uk/og-default.jpg?v=logo2" jsonLd={homeJsonLd} />
      <LandingHeader lifetimeHref={lifetimeHref} soldOut={stats?.sold_out} />
      <main>
        <LandingHero loaded={loaded} heroVariant={heroVariant} />
        <LandingChoice lifetimeHref={lifetimeHref} soldOut={stats?.sold_out} price={stats?.price || 25} variant="hero" />
        <NewInVaultAnnouncement variant="landing" />
        <BirthdayBanner />
        <LandingNarrative lifetimeHref={lifetimeHref} />
        <LandingProductPreview />
        <section className="border-b border-white/10 bg-[#07060a]" data-testid="landing-product-proof">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:px-10 lg:py-28"><ChatGPTOutputShowcase /></div>
        </section>
        <LandingTrackPreview lifetimeHref={lifetimeHref} />
        <LandingLifetimeOffer stats={stats} lifetimeHref={lifetimeHref} />
        <div id="what-could-you-build"><WhatCouldYouBuild /></div>
        <LandingProofBand />
        <BirthdayPromoSection />
        <section className="border-b border-white/10 bg-[#030206]" data-testid="homepage-fast-monetisation-section">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:px-10 lg:py-20"><FastMonetisationDiscoveryBanner source="homepage_feature" rootTestId="homepage-fast-monetisation-banner" ctaTestId="homepage-fast-monetisation-banner-cta" /></div>
        </section>
        <FounderBlock />
        <LandingFaq />
        <LandingChoice lifetimeHref={lifetimeHref} soldOut={stats?.sold_out} price={stats?.price || 25} variant="final" />
      </main>
      <LandingStickyChoiceBar lifetimeHref={lifetimeHref} soldOut={stats?.sold_out} price={stats?.price || 25} />
      <SiteFooter />
    </div>
  );
}